import React, { useEffect, useRef, useState, useContext, useMemo } from "react";
import { useLocation } from "@reach/router";
import Camera from "../visual-search/camera";
import search from "@root/assets/svgs/search.svg";
import lockBodyScroll from "@helpers/lockBodyScroll";
import aiCamera from "@root/assets/svgs/ai-camera.svg";
import { SearchContext } from "context/search";
import { CategoryContext } from "@root/context/category";
import { VisualSearchContext } from "@root/context/visual-search";
import TypingPlaceholder from "../visual-search/typing-placeholder";
const VisualSearch = () => {
    const [open, setOpen] = useState(false);
    const [showTooltip, setShowTooltip] = useState(true);
    const [shouldRenderTooltip, setShouldRenderTooltip] = useState(true);
    const inputRef = useRef(null);
    const fileInputRef = useRef(null);
    const { baseCategory } = useContext(CategoryContext) || {};
    const [results, setResults] = useState([]);
    const [uploadedPhoto, setUploadedPhoto] = useState(null);
    const location = useLocation();
    const { visualSearchState, saveVisualSearchState } = useContext(VisualSearchContext) || {};
    const [isMounted, setIsMounted] = useState(false);

    useEffect(() => {
        if (baseCategory) {
            const semiSubcategories = baseCategory.subcategories.flatMap(category => category.subcategories.slice(0, 2));
            const subcategories = baseCategory.subcategories;
            setResults(semiSubcategories.length > 0 ? semiSubcategories : subcategories);
        }
    }, []);

    const TEXT_MAP = {
        industrial: [
            "AC Unit Covers",
            "Infrared Heater Covers",
            "Heating System Covers",
            "Ventilation Covers",
        ],
        grill: [
            "Chiminea Covers",
            "Fire Bowl Covers",
            "Fire Column Covers",
        ],
        default: [
            "Furniture Covers",
            "Sectional Sofa Covers",
            "Couch Covers",
            "Ottoman Covers",
            "Adirondack Covers",
        ],
    };


    useEffect(() => {
        setIsMounted(true);
    }, []);

    useEffect(() => {
        const FADE_DURATION_MS = 300;
        const timer = setTimeout(() => {
            setShowTooltip(false);
        }, 5000);

        const removeTimer = setTimeout(() => {
            setShouldRenderTooltip(false);
        }, 5000 + FADE_DURATION_MS);

        return () => {
            clearTimeout(timer);
            clearTimeout(removeTimer);
        };
    }, []);

    const texts = useMemo(() => {
        if (!isMounted || !location) return TEXT_MAP.default;

        const path = location.pathname.toLowerCase();
        const key = Object.keys(TEXT_MAP).find(k => path.includes(k));
        return TEXT_MAP[key] || TEXT_MAP.default;
    }, [isMounted]);

    const { openSearch, closeSearch, isMobileSearchOpen } = useContext(SearchContext);

    lockBodyScroll(open, true);

    useEffect(() => {
        console.log("new use effect");
    }, []);

    const handleSearchClick = () => {
        if (inputRef.current) {
            const rect = inputRef.current.getBoundingClientRect();
            const triggerPosition = {
                top: rect.top,
                left: rect.left,
                width: rect.width,
                height: rect.height
            };
            openSearch(triggerPosition);
        } else {
            openSearch();
        }
    };

    // useEffect(() => {
    //     const currentText = texts[textIndex];
    //     const typingSpeed = isDeleting ? 50 : 100;

    //     const timer = setTimeout(() => {
    //         if (!isDeleting && currentIndex < currentText.length) {
    //             setPlaceholderText(currentText.slice(0, currentIndex + 1));
    //             setCurrentIndex(currentIndex + 1);
    //         } else if (isDeleting && currentIndex > 0) {
    //             setPlaceholderText(currentText.slice(0, currentIndex - 1));
    //             setCurrentIndex(currentIndex - 1);
    //         } else if (!isDeleting && currentIndex === currentText.length) {
    //             setTimeout(() => setIsDeleting(true), 2000);
    //         } else if (isDeleting && currentIndex === 0) {
    //             setIsDeleting(false);
    //             setTextIndex((textIndex + 1) % texts.length);
    //         }
    //     }, typingSpeed);

    //     return () => clearTimeout(timer);
    // }, [currentIndex, isDeleting, textIndex, texts]);

    useEffect(() => {
        const currentPath = location.pathname;
        const savedPath = visualSearchState?.sourcePath;

        if (visualSearchState?.photo && savedPath === currentPath) {
            setUploadedPhoto(visualSearchState.photo);
            setOpen(true);
        }
    }, [location.pathname, visualSearchState?.photo, visualSearchState?.sourcePath]);

    const close = () => {
        setOpen(false);
        setUploadedPhoto(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleCategoryClick = (value) => {
        openSearch(null, value.name);
    }

    const handleFileUpload = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            event.target.value = '';
            return;
        }

        try {
            // Read file as data URL
            const reader = new FileReader();
            reader.onload = (e) => {
                const dataURL = e.target.result;
                if (typeof window !== 'undefined') {
                    saveVisualSearchState?.({ sourcePath: location.pathname });
                }
                setUploadedPhoto(dataURL);
                setOpen(true);
            };

            reader.onerror = () => {
                event.target.value = '';
            };

            reader.readAsDataURL(file);
        } catch (err) {
            console.error('Error reading file:', err);
            event.target.value = '';
        }
    };

    // Determine which overlay to show
    const showCamera = open && !uploadedPhoto;
    const showUploadedImage = open && uploadedPhoto;

    const handleCameraOpen = () => {
        if (typeof window !== 'undefined') {
            saveVisualSearchState?.({ sourcePath: location.pathname });
        }
        setOpen(true);
    };

    return (
        <div className="image-search">
            <div className="image-search__card">
                <div className="image-search__actions">
                    <div className="image-search__actions-left">
                        <div onClick={handleSearchClick} className="image-search__actions-left-input">
                            <img src={search} alt="search" />
                            <div
                                ref={inputRef}
                                className="image-search__actions-left-input-text"
                            >
                                <TypingPlaceholder texts={texts} />
                            </div>
                        </div>
                    </div>
                    <div className="image-search__actions-right">
                        <div className="image-search__tooltip-container">
                            <button onClick={handleCameraOpen} className="image-search__camera-button">
                                <img src={aiCamera} alt="yellow-camera" />
                                <p>Snap AI</p>
                            </button>
                            {shouldRenderTooltip && (
                                <div
                                    className={`image-search__tooltip-content ${showTooltip ? "" : "image-search__tooltip-content--hidden"}`}
                                >
                                    <span style={{ whiteSpace: "nowrap" }}>
                                        Finding the right cover just got easier.
                                        <br />
                                        One quick snap. Get an instant match.
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Camera or Uploaded Image overlay */}
            {(showCamera || showUploadedImage) && (
                <div className="image-search__overlay">
                    <Camera close={close} initialPhoto={uploadedPhoto} />
                </div>
            )}
        </div>
    );
};

export default VisualSearch;